// src/hooks/useScanQuota.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { scanQuotaService, ScanQuota } from '@/services/scanQuotaService';
import { auth } from '@/lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { toast } from '@/components/ui/use-toast';

export function useScanQuota(refreshInterval: number = 0) {
  const [quota, setQuota]       = useState<ScanQuota | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]       = useState<Error | null>(null);

  const authListenerActive     = useRef(false);
  const refreshTimerRef        = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef           = useRef(true);
  const lastFetchAttemptRef    = useRef(0);
  const lastAuthChangeRef      = useRef(0);
  const retryTimeoutRef        = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef          = useRef(0);

  const MIN_FETCH_INTERVAL       = 3000;  // 3s
  const MIN_AUTH_REFRESH_INTERVAL = 5000;  // 5s
  const MAX_RETRIES              = 3;

  /**
   * Fetch quota with rate‐limiting, retry & backoff.
   * NOTE: no `isLoading` in deps → this fn identity stays stable.
   */
  const fetchQuota = useCallback(async (forceRefresh = false) => {
    // Prevent overlapping loads (optional)
    if (isLoading && !forceRefresh) return;
    if (!isMountedRef.current) return;

    // Global rate‐limit
    const now = Date.now();
    if (!forceRefresh && now - lastFetchAttemptRef.current < MIN_FETCH_INTERVAL) {
      console.log('Fetch quota request rate limited');
      return;
    }
    lastFetchAttemptRef.current = now;

    setIsLoading(true);
    setError(null);

    try {
      console.log(`Fetching quota data${forceRefresh ? ' (forced)' : ''}, retry #${retryCountRef.current}`);
      const result = await scanQuotaService.getQuota(forceRefresh);

      if (isMountedRef.current) {
        setQuota(result);
        retryCountRef.current = 0;
        console.log('Successfully updated quota data:', result);
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      const errorObj = err instanceof Error ? err : new Error('Failed to fetch scan quota');
      setError(errorObj);
      console.error('Error fetching scan quota:', errorObj);

      // Retry with exponential backoff
      if (retryCountRef.current < MAX_RETRIES && !retryTimeoutRef.current) {
        retryCountRef.current++;
        const delay = 2000 * Math.pow(2, retryCountRef.current - 1);
        console.log(`Scheduling retry #${retryCountRef.current} after ${delay}ms`);
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          if (isMountedRef.current) fetchQuota(true);
        }, delay);
      } else if (retryCountRef.current >= MAX_RETRIES) {
        toast({
          title: "Error",
          description: "Could not retrieve scan quota. Please try again later.",
          variant: "destructive"
        });
        console.error(`Maximum retries (${MAX_RETRIES}) reached for quota fetch`);
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, []); // <- NO isLoading here

  // Expose a manual refresh that forces a network call
  const refreshQuota = useCallback(() => {
    console.log('Manually refreshing quota data');
    return fetchQuota(true);
  }, [fetchQuota]);

  // When auth changes, clear cache & re-fetch
  const handleAuthStateChange = useCallback(() => {
    if (!isMountedRef.current) return;

    const now = Date.now();
    if (now - lastAuthChangeRef.current < MIN_AUTH_REFRESH_INTERVAL) {
      console.log('Auth refresh rate limited, skipping');
      return;
    }
    lastAuthChangeRef.current = now;

    console.log('Auth state changed, invalidating and refreshing quota');
    scanQuotaService.invalidateCache();
    setTimeout(() => {
      if (isMountedRef.current) fetchQuota(true);
    }, 1500);
  }, [fetchQuota]);

  // On mount: initial fetch, auth listener, optional polling
  useEffect(() => {
    isMountedRef.current = true;
    const initTimer = setTimeout(() => {
      if (isMountedRef.current) fetchQuota();
    }, 800);

    let unsubscribe = () => {};
    if (!authListenerActive.current) {
      authListenerActive.current = true;
      unsubscribe = onAuthStateChanged(auth, handleAuthStateChange);

      if (refreshInterval > 0) {
        refreshTimerRef.current = setInterval(() => {
          if (isMountedRef.current) fetchQuota();
        }, refreshInterval);
      }
    }

    return () => {
      isMountedRef.current = false;
      unsubscribe();
      clearTimeout(initTimer);
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [fetchQuota, handleAuthStateChange, refreshInterval]);

  return {
    quota,
    isLoading,
    error,
    refreshQuota,
    hasAvailableScans: Boolean(quota && (quota.isSubscribed || quota.remainingScans > 0)),
    isGuest: Boolean(quota?.isGuest),
    isSubscribed: Boolean(quota?.isSubscribed),
    remainingScans: quota?.remainingScans ?? 0,
  };
}

export default useScanQuota;
